import './cellRenderers.css';
import PropTypes from 'prop-types';
import { Avatar, Tooltip } from 'antd';
import { Icon } from '@icp/components';
import withCurrentData from './withCurrentData';
import { isEmptyValue, getEmptyPlaceholder } from './cellRendererUtils';

function renderGroup({ images, tableSize }) {
  const groupSize = (tableSize === 'large' && 'default') || 'small';

  return (
    <Avatar.Group
      size={groupSize}
      max={{
        count: 2,
        style: { color: '#f56a00', backgroundColor: '#fde3cf' },
      }}
    >
      {images.map((item) => {
        const reactKey = `${item.avatar}${item.label}`;
        return item.label ? (
          <Tooltip key={reactKey} title={item.label}>
            <Avatar size={groupSize} src={item.avatar} />
          </Tooltip>
        ) : (
          <Avatar key={reactKey} size={groupSize} src={item.avatar} />
        );
      })}
    </Avatar.Group>
  );
}

function renderAvatar({ images, tableSize }) {
  return images[0]?.avatar ? (
    <Avatar size={tableSize} src={images[0].avatar} />
  ) : (
    <Avatar size={tableSize} icon={<Icon name="ant:user-outlined" />} />
  );
}

function renderAvatarLabel({ images, tableSize }) {
  return (
    <>
      {images[0]?.avatar ? (
        <Avatar size={tableSize} src={images[0].avatar} />
      ) : (
        <Avatar size={tableSize} icon={<Icon name="ant:user-outlined" />} />
      )}
      {images[0]?.label ? <span className="avatar-cell-label">{images[0].label}</span> : null}
    </>
  );
}

function AvatarRenderer(props) {
  const { value, context, avatarStyle = 'auto', emptyPlaceholder } = props;

  if (emptyPlaceholder && isEmptyValue(value)) {
    return getEmptyPlaceholder(emptyPlaceholder);
  }

  const { tableSize } = context;

  const images = AvatarRenderer.getImages(value);

  return (
    <div className="table-avatar-cell">
      {(avatarStyle === 'group' && renderGroup({ images, tableSize })) ||
        (avatarStyle === 'avatar' && renderAvatar({ images, tableSize })) ||
        (avatarStyle === 'avatar-label' && renderAvatarLabel({ images, tableSize })) ||
        (images.length === 1 && renderAvatarLabel({ images, tableSize })) ||
        (images.length > 1 && renderGroup({ images, tableSize })) ||
        null}
    </div>
  );
}

AvatarRenderer.getImages = (value) => {
  // 普通数据，当 avatar 处理
  if (typeof value === 'string') {
    return [{ avatar: value }];
  }

  // Upload 组件的列的原始数据
  if (Array.isArray(value?.resources)) {
    return value.resources.map((item) => {
      return { avatar: item.url };
    });
  }

  // [{ avatar, label }] 也需要自己进行数据转换转出来
  if (Array.isArray(value)) {
    return value.map((item) => {
      return { avatar: item.avatar, label: item.label };
    });
  }

  // { avatar, label } 格式需要自己进行数据转换转出来
  if (value && typeof value === 'object') {
    return [{ avatar: value.avatar, label: value.label }];
  }

  return [];
};

AvatarRenderer.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      avatar: PropTypes.string,
      label: PropTypes.string,
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        avatar: PropTypes.string,
        label: PropTypes.string,
      }),
    ),
    PropTypes.shape({
      resources: PropTypes.arrayOf(
        PropTypes.shape({
          avatar: PropTypes.string,
        }),
      ),
    }),
  ]),
  context: PropTypes.shape({
    tableSize: PropTypes.string,
  }),
  /**
   * 当值为空时显示的占位符。传 true 默认显示 '-'，传字符串则显示自定义内容
   */
  emptyPlaceholder: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  avatarStyle: PropTypes.oneOf(['auto', 'avatar', 'avatar-label', 'group']),
};

export default withCurrentData(AvatarRenderer);
